import { useState, useEffect } from "react";
import {
  Plus,
  RefreshCw,
  Calendar,
  BarChart3,
  Clock,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";

// Components
import TimeClockWidget from "./components/TimeClockWidget";
import AttendanceStats from "./components/AttendanceStats";
import AttendanceCalendar from "./components/AttendanceCalendar";
import AbsenceRequestForm from "./components/AbsenceRequestForm";
import AbsenceRequestsTable from "./components/AbsenceRequestsTable";

// API
import { attendanceAPI } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";

export default function AttendancePage() {
  const { toast } = useToast();

  // State management
  const [activeTab, setActiveTab] = useState("overview");
  const [showAbsenceForm, setShowAbsenceForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Data state
  const [attendanceData, setAttendanceData] = useState([]);
  const [absenceRequests, setAbsenceRequests] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalEmployees: 0,
    presentToday: 0,
    absentToday: 0,
    lateToday: 0,
    avgWorkingHours: 0,
    overtimeThisMonth: 0,
  });

  // Auth context
  const { user } = useAuth();
  const userRole = user?.role || "employe";
  const currentUserId = user?.id;
  const canManageAttendance = ["admin", "rh", "manager"].includes(userRole);

  // Load personal attendance history for calendar
  const loadPersonalAttendanceHistory = async () => {
    try {
      // Get the current month's start and end dates
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      // Format dates as YYYY-MM-DD
      const start_date = firstDay.toISOString().split("T")[0];
      const end_date = lastDay.toISOString().split("T")[0];

      const response = await attendanceAPI.getMyAttendanceHistory({
        start_date,
        end_date,
        limit: 100, // Get enough records for the month
      });

      if (response.data.success) {
        setAttendanceData(response.data.data || []);
      }
    } catch (err) {
      console.error("Error loading personal attendance history:", err);
      // Don't show error toast for this, as it's secondary data
    }
  };

  // Load all data
  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      // Load dashboard statistics
      const dashboardResponse = await attendanceAPI.getEnhancedDashboard({
        period: "today",
      });
      if (dashboardResponse.data.success) {
        const data = dashboardResponse.data.data;

        // Handle different data structure for employees vs admin
        if (data.userRole === "employe") {
          setDashboardStats({
            totalAbsences: data.totalAbsences || 0,
            totalPresences: data.totalPresences || 0,
            isLateToday: data.isLateToday || false,
            avgWorkingHours: data.avgWorkingHours || 0,
            overtimeThisMonth: data.overtimeThisMonth || 0,
            userRole: data.userRole,
          });
        } else {
          setDashboardStats({
            totalEmployees: data.totalEmployees || 0,
            presentToday: data.presentToday || 0,
            absentToday: data.absentToday || 0,
            lateToday: data.lateToday || 0,
            avgWorkingHours: data.avgWorkingHours || 0,
            overtimeThisMonth: data.overtimeThisMonth || 0,
            userRole: data.userRole,
          });
        }

        // Load personal attendance history for calendar (separate call)
        await loadPersonalAttendanceHistory();
      }

      // Load absence requests
      const absenceResponse = await attendanceAPI.getAbsenceRequests({
        status: "all",
        limit: 50,
      });
      if (absenceResponse.data.success) {
        setAbsenceRequests(absenceResponse.data.data || []);
      }

      // Load absence types
      const typesResponse = await attendanceAPI.getAbsenceTypes();
      if (typesResponse.data.success) {
        setAbsenceTypes(typesResponse.data.data || []);
      }
    } catch (err) {
      console.error("Error loading attendance data:", err);
      setError("Erreur lors du chargement des données de présence.");
      toast({
        title: "Erreur",
        description: "Impossible de charger les données de présence",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    loadData();
  };

  // Handle absence form success
  const handleAbsenceSuccess = () => {
    setShowAbsenceForm(false);
    loadData(); // Refresh data
    toast({
      title: "Succès",
      description: "Demande d'absence créée avec succès",
    });
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Filter absence data for calendar
  const approvedAbsences = absenceRequests.filter(
    (req) => req.statut === "validee"
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">
            Chargement des données de présence...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Présence
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Gestion de la présence et des absences
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
              />
              Actualiser
            </Button>
            <Button onClick={() => setShowAbsenceForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Demande d'absence
            </Button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="border-b border-gray-200 dark:border-gray-700 px-6">
            <TabsList className="grid w-full grid-cols-4 bg-transparent p-0 h-auto">
              <TabsTrigger
                value="overview"
                className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                <BarChart3 className="h-4 w-4" />
                <span className="hidden sm:inline">Vue d'ensemble</span>
              </TabsTrigger>
              <TabsTrigger
                value="clock"
                className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                <Clock className="h-4 w-4" />
                <span className="hidden sm:inline">Pointage</span>
              </TabsTrigger>
              <TabsTrigger
                value="calendar"
                className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                <Calendar className="h-4 w-4" />
                <span className="hidden sm:inline">Calendrier</span>
              </TabsTrigger>
              <TabsTrigger
                value="requests"
                className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Demandes</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <AttendanceStats
                stats={dashboardStats}
                loading={loading}
                userRole={userRole}
              />

              {canManageAttendance && (
                <Card>
                  <CardHeader>
                    <CardTitle>Aperçu des demandes récentes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {absenceRequests.length > 0 ? (
                      <div className="space-y-3">
                        {absenceRequests.slice(0, 5).map((request) => (
                          <div
                            key={request.id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div>
                                <p className="font-medium">
                                  {request.employe?.prenom}{" "}
                                  {request.employe?.nom}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {request.type_absence?.nom} -{" "}
                                  {new Date(
                                    request.date_debut
                                  ).toLocaleDateString("fr-FR")}
                                </p>
                              </div>
                            </div>
                            <Badge
                              variant={
                                request.statut === "validee"
                                  ? "default"
                                  : request.statut === "contestee"
                                  ? "destructive"
                                  : "secondary"
                              }
                            >
                              {request.statut === "declaree"
                                ? "En attente"
                                : request.statut === "validee"
                                ? "Approuvé"
                                : "Refusé"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">
                        Aucune demande récente
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Clock Tab */}
            <TabsContent value="clock" className="space-y-6">
              <TimeClockWidget onAttendanceUpdate={handleRefresh} />
            </TabsContent>

            {/* Calendar Tab */}
            <TabsContent value="calendar" className="space-y-6">
              <AttendanceCalendar
                attendanceData={attendanceData}
                absenceData={approvedAbsences}
                userRole={userRole}
              />
            </TabsContent>

            {/* Requests Tab */}
            <TabsContent value="requests" className="space-y-6">
              <AbsenceRequestsTable
                requests={absenceRequests}
                loading={loading}
                onRefresh={handleRefresh}
                userRole={userRole}
                currentUserId={currentUserId}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Absence Request Form Modal */}
      <AbsenceRequestForm
        isOpen={showAbsenceForm}
        onClose={() => setShowAbsenceForm(false)}
        onSuccess={handleAbsenceSuccess}
        absenceTypes={absenceTypes}
        userRole={userRole}
      />
    </div>
  );
}
